import { useState, useCallback } from "react";
import { PROMPT_CONSTANTS } from "../constants/promptConstants";

const VALID_NAME_REGEX = /^[a-zA-Z0-9 ]+$/;

export const usePromptValidation = (existingNames = [], maxInstructionsLength = PROMPT_CONSTANTS.MAX_INSTRUCTIONS_LENGTH) => {
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    category: "",
    llmModel: "",
    instructions: "",
  });

  const [formState, setFormState] = useState({
    name: "default",
    description: "default", 
    category: "default",
    llmModel: "default",
    instructions: "default",
  });

  const validateName = useCallback((name) => {
    if (!name.trim()) {
      setErrors((prev) => ({ ...prev, name: "Name is required" }));
      setFormState((prev) => ({ ...prev, name: "error" }));
      return false;
    }

    if (!VALID_NAME_REGEX.test(name)) {
      setErrors((prev) => ({
        ...prev,
        name: "You can only use alphanumeric characters and spaces",
      }));
      setFormState((prev) => ({ ...prev, name: "error" }));
      return false;
    }

    if (existingNames.includes(name.trim())) {
      setErrors((prev) => ({
        ...prev,
        name: "This name already exists, try another name or add a variation to it",
      }));
      setFormState((prev) => ({ ...prev, name: "error" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, name: "" }));
    setFormState((prev) => ({ ...prev, name: "default" }));
    return true;
  }, [existingNames]);

  const validateDescription = useCallback((description) => {
    if (description && description.length > PROMPT_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
      setErrors((prev) => ({
        ...prev,
        description: `Description must be under ${PROMPT_CONSTANTS.MAX_DESCRIPTION_LENGTH} characters`,
      }));
      setFormState((prev) => ({ ...prev, description: "error" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, description: "" }));
    setFormState((prev) => ({ ...prev, description: "default" }));
    return true;
  }, []);

  const validateCategory = useCallback((category) => {
    setErrors((prev) => ({ ...prev, category: "" }));
    setFormState((prev) => ({ ...prev, category: "default" }));
    return true;
  }, []);

  const validateLlmModel = useCallback((llmModel) => {
    if (!llmModel) {
      setErrors((prev) => ({ ...prev, llmModel: "LLM Model is required" }));
      setFormState((prev) => ({ ...prev, llmModel: "error" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, llmModel: "" }));
    setFormState((prev) => ({ ...prev, llmModel: "default" }));
    return true;
  }, []);

  const validateInstructions = useCallback((text) => {
    if (!text.trim()) {
      setErrors((prev) => ({ ...prev, instructions: "Instructions are required" }));
      setFormState((prev) => ({ ...prev, instructions: "error" }));
      return false;
    }

    if (text.length >= maxInstructionsLength) {
      setErrors((prev) => ({
        ...prev,
        instructions: `Instructions must be under ${maxInstructionsLength} characters`,
      }));
      setFormState((prev) => ({ ...prev, instructions: "error" }));
      return false;
    }

    setErrors((prev) => ({ ...prev, instructions: "" }));
    setFormState((prev) => ({ ...prev, instructions: "default" }));
    return true;
  }, [maxInstructionsLength]);

  const validateAll = useCallback((formData) => {
    const nameValid = validateName(formData.name);
    const descriptionValid = validateDescription(formData.description);
    const categoryValid = validateCategory(formData.category);
    const llmModelValid = validateLlmModel(formData.llmModel);
    const instructionsValid = validateInstructions(formData.instructions);

    return nameValid && descriptionValid && categoryValid && llmModelValid && instructionsValid;
  }, [validateName, validateDescription, validateCategory, validateLlmModel, validateInstructions]);

  const clearErrors = useCallback(() => {
    setErrors({ name: "", description: "", category: "", llmModel: "", instructions: "" });
    setFormState({ name: "default", description: "default", category: "default", llmModel: "default", instructions: "default" });
  }, []);

  return {
    errors,
    formState,
    validateName,
    validateDescription,
    validateCategory,
    validateLlmModel,
    validateInstructions,
    validateAll,
    clearErrors,
  };
};
